import type { StatsUnit } from '@/shared/api/types';
import { addDays, addMonthsToMonthStart, kstToday, weekStart } from '@/shared/lib/date';

type RangePreset = { value: string; label: string; count: number };

/**
 * 단위별 기간 프리셋 — 서버 최대 기간(일 92일 · 주 53주 · 월 24개월) 안에서 고른다.
 * 첫 값이 그 단위의 기본값이다.
 */
export const RANGE_PRESETS: Record<StatsUnit, readonly RangePreset[]> = {
  day: [
    { value: '30d', label: '30일', count: 30 },
    { value: '7d', label: '7일', count: 7 },
    { value: '90d', label: '90일', count: 90 },
  ],
  week: [
    { value: '12w', label: '12주', count: 12 },
    { value: '26w', label: '26주', count: 26 },
    { value: '52w', label: '52주', count: 52 },
  ],
  month: [
    { value: '12m', label: '12개월', count: 12 },
    { value: '6m', label: '6개월', count: 6 },
    { value: '24m', label: '24개월', count: 24 },
  ],
};

export const UNIT_OPTIONS = [
  { value: 'day', label: '일' },
  { value: 'week', label: '주' },
  { value: 'month', label: '월' },
] as const satisfies readonly { value: StatsUnit; label: string }[];

/** 화면에 보이는 순서(짧은 기간 → 긴 기간) */
export function rangeOptions(unit: StatsUnit): { value: string; label: string }[] {
  return [...RANGE_PRESETS[unit]]
    .sort((a, b) => a.count - b.count)
    .map(({ value, label }) => ({ value, label }));
}

/** 단위에 맞지 않는 프리셋이면 그 단위의 기본값으로 바꾼다 (주소창에서 단위만 바꾼 경우 등) */
export function normalizeRange(unit: StatsUnit, range: string | undefined): string {
  const presets = RANGE_PRESETS[unit];
  const matched = presets.find(preset => preset.value === range);
  return (matched ?? presets[0])?.value ?? '';
}

/**
 * 프리셋의 시작일 — 기준은 어제(D-1, 스냅샷이 쌓인 마지막 날).
 * 주·월 단위는 버킷 첫날(월요일·1일)에 맞춰 첫 막대가 잘리지 않게 한다. 종료일은 서버가 기준일로 잡는다.
 */
export function resolveRangeFrom(unit: StatsUnit, range: string, today = kstToday()): string {
  const preset = RANGE_PRESETS[unit].find(item => item.value === range) ?? RANGE_PRESETS[unit][0];
  const count = preset?.count ?? 1;
  const base = addDays(today, -1);
  if (unit === 'week') return addDays(weekStart(base), -(count - 1) * 7);
  if (unit === 'month') return addMonthsToMonthStart(base, -(count - 1));
  return addDays(base, -(count - 1));
}
